function toFiniteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function formatProductMoney(value, currency = "GBP") {
  const number = toFiniteNumber(value);
  if (number === null) return "";

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: String(currency || "GBP").trim() || "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

export function getVariantPrices(product = {}) {
  if (!Array.isArray(product?.variants)) return [];

  const pricedVariants = product.variants
    .map((variant) => ({
      price: toFiniteNumber(variant?.price),
      variantType: String(variant?.variantType || variant?.type || "")
        .trim()
        .toLowerCase(),
    }))
    .filter((variant) => variant.price !== null && variant.price > 0);

  const nonSampleVariants = pricedVariants.filter(
    (variant) => variant.variantType !== "sample",
  );

  return (nonSampleVariants.length ? nonSampleVariants : pricedVariants).map(
    (variant) => variant.price,
  );
}

function getProductDisplayPriceInfo(product = {}) {
  const priceFrom = toFiniteNumber(product?.priceFrom);
  const price = toFiniteNumber(product?.price);
  const variantPrices = getVariantPrices(product);
  const variantMinPrice = variantPrices.length
    ? Math.min(...variantPrices)
    : null;

  if (product?.isVariant) {
    if (priceFrom !== null && priceFrom > 0) {
      return { price: priceFrom, isFrom: true };
    }
    if (variantMinPrice !== null) {
      return { price: variantMinPrice, isFrom: true };
    }
    if (price !== null && price > 0) return { price, isFrom: false };

    return null;
  }

  if (price !== null && price > 0) return { price, isFrom: false };
  if (priceFrom !== null && priceFrom > 0) return { price: priceFrom, isFrom: false };
  if (variantMinPrice !== null) return { price: variantMinPrice, isFrom: true };

  return null;
}

export function getProductDisplayPriceValue(product = {}) {
  return getProductDisplayPriceInfo(product)?.price ?? null;
}

export function getFirstPositivePrice(...values) {
  for (const value of values) {
    const number = toFiniteNumber(value);
    if (number !== null && number > 0) return number;
  }

  return null;
}

export function getProductDisplayPrice(product = {}, options = {}) {
  const priceInfo = getProductDisplayPriceInfo(product);
  if (!priceInfo) return "";

  const formatted = formatProductMoney(
    priceInfo.price,
    options.currency || product?.currency,
  );
  if (!formatted) return "";

  return priceInfo.isFrom && options.showFrom !== false
    ? `From ${formatted}`
    : formatted;
}
